import DummyRouterView from "@/components/common/DummyRouterView.vue";
import { RouteLocationNormalized, RouteRecordRaw } from "vue-router";

import {
  numericParser,
  monthPeriodParser,
  nullableMonthPeriodParser,
  nullableNumericParser,
} from "@/router/parsers";

import { authorizationMiddleware } from "../middleware";
import store from "@/store";

const routes: RouteRecordRaw = {
  path: "entidades",
  name: "admin-entities-dummy",
  component: DummyRouterView,
  children: [
    {
      path: "",
      name: "admin-entities",
      component: () =>
        import(
          /* webpackChunkName: "admin" */ "../../views/admin/entity/Index.vue"
        ),
      meta: {
        middlewares: [authorizationMiddleware],
        permissions: new Map([
          [
            "entity/view-any",
            { userRoleId: () => store.state.authUser?.user?.roleId },
          ],
        ]),
      },
    },
    {
      path: "crear",
      name: "admin-entities-create",
      component: () =>
        import(
          /* webpackChunkName: "admin" */ "../../views/admin/entity/Create.vue"
        ),
      meta: {
        middlewares: [authorizationMiddleware],
        permissions: new Map([
          [
            "entity/create",
            { userRoleId: () => store.state.authUser?.user?.roleId },
          ],
        ]),
      },
    },
    {
      path: ":entityId",
      name: "admin-entities-show",
      component: () =>
        import(
          /* webpackChunkName: "admin" */ "../../views/admin/entity/Show.vue"
        ),
      props: (
        route: RouteLocationNormalized
      ): Record<string, number | string> => {
        return {
          ...numericParser(route, "entityId"),
        };
      },
      meta: {
        middlewares: [authorizationMiddleware],
        permissions: new Map([
          [
            "entity/view",
            {
              entityId: "from-params",
              userEntityId: () => store.state.authUser?.user?.entityId,
              userRoleId: () => store.state.authUser?.user?.roleId,
            },
          ],
        ]),
      },
    },
    {
      path: ":entityId/editar",
      name: "admin-entities-edit",
      component: () =>
        import(
          /* webpackChunkName: "admin" */ "../../views/admin/entity/Edit.vue"
        ),
      props: true,
      meta: {
        middlewares: [authorizationMiddleware],
        permissions: new Map([
          [
            "entity/edit",
            { userRoleId: () => store.state.authUser?.user?.roleId },
          ],
        ]),
      },
    },

    {
      path: ":entityId/eliminar",
      name: "admin-entities-delete",
      meta: {
        middlewares: [authorizationMiddleware],
        permissions: new Map([
          [
            "entity/edit", // add delete policy
            { userRoleId: () => store.state.authUser?.user?.roleId },
          ],
        ]),
      },
      component: () =>
        import(
          /* webpackChunkName: "admin" */ "../../views/admin/entity/Delete.vue"
        ),
      props: true,
    },

    {
      path: ":entityId/departamentos",
      name: "admin-entities-departments",
      component: () =>
        import(
          /* webpackChunkName: "admin" */ "../../views/admin/entity/ManageDepartments.vue"
        ),
      props: true,
      meta: {
        middlewares: [authorizationMiddleware],
        permissions: new Map([
          [
            "entity/department",
            { userRoleId: () => store.state.authUser?.user?.roleId },
          ],
        ]),
      },
    },
    {
      path: ":entityId/usuarios/crear",
      name: "admin-entities-users-create",
      component: () =>
        import(
          /* webpackChunkName: "admin" */ "../../views/admin/entity/UserCreate.vue"
        ),
      props: (route) => ({ ...numericParser(route, "entityId") }),
      meta: {
        middlewares: [authorizationMiddleware],
        permissions: new Map([
          [
            "entity/create-users",
            { userRoleId: () => store.state.authUser?.user?.roleId },
          ],
        ]),
      },
    },
    {
      path: ":entityId/usuarios/lista",
      name: "admin-entities-users-list",
      component: () =>
        import(
          /* webpackChunkName: "admin" */ "../../views/admin/entity/UserList.vue"
        ),
      props: (route) => ({ ...numericParser(route, "entityId") }),
      meta: {
        middlewares: [authorizationMiddleware],
        permissions: new Map([
          [
            "entity/view-users",
            {
              entityId: "from-params",
              userRoleId: () => store.state.authUser?.user?.roleId,
              userEntityId: () => store.state.authUser?.user?.entityId,
            },
          ],
        ]),
      },
    },
    {
      path: ":entityId/usuarios/:userId/editar",
      name: "admin-entities-user-edit",
      component: () =>
        import(
          /* webpackChunkName: "admin" */ "../../views/admin/entity/UserEdit.vue"
        ),
      props: (route) => ({
        ...numericParser(route, "entityId"),
        ...numericParser(route, "userId"),
      }),
      meta: {
        middlewares: [authorizationMiddleware],
        permissions: new Map([
          [
            "entity/edit-users",
            {
              entityId: "from-params",
              userRoleId: () => store.state.authUser?.user?.roleId,
            },
          ],
        ]),
      },
    },
    {
      path: ":entityId/entregar/:entityDocId/:period",
      name: "admin-entities-submit-doc",
      component: () =>
        import(
          /* webpackChunkName: "admin" */ "../../views/admin/entity/SubmitDocumentation.vue"
        ),
      props: (route: RouteLocationNormalized): Record<string, unknown> => ({
        ...numericParser(route, "entityId"),
        ...numericParser(route, "entityDocId"),
        ...monthPeriodParser(route, "period"),
      }),
      meta: {
        middlewares: [authorizationMiddleware],
        permissions: new Map([
          [
            "submission/create",
            {
              entityId: "from-params",
              userEntityId: () => store.state.authUser?.user?.entityId,
              userRoleId: () => store.state.authUser?.user?.roleId,
            },
          ],
        ]),
      },
    },
    {
      path: ":entityId/docs-config",
      name: "admin-entities-doc-config-index",
      component: () =>
        import(
          /* webpackChunkName: "admin" */ "../../views/admin/entity/documentation/settings/Index.vue"
        ),
      props: (route) => ({
        ...numericParser(route, "entityId"),
      }),
      meta: {
        middlewares: [authorizationMiddleware],
        permissions: new Map([
          //TODO: cambiar policy a entity-doc-settings
          [
            "submission/view",
            {
              entityId: "from-params",
              userRoleId: () => store.state.authUser?.user?.roleId,
              userEntityId: () => store.state.authUser?.user?.entityId,
            },
          ],
        ]),
      },
    },
    {
      path: ":entityId/docs-config/:entityDocId",
      name: "admin-entities-doc-config",
      component: () =>
        import(
          /* webpackChunkName: "admin" */ "../../views/admin/entity/documentation/settings/Edit.vue"
        ),
      props: (route) => ({
        ...numericParser(route, "entityId"),
        ...numericParser(route, "entityDocId"),
      }),
      meta: {
        middlewares: [authorizationMiddleware],
        permissions: new Map([
          //TODO: cambiar policy a entity-doc-settings
          [
            "submission/view",
            {
              entityId: "from-params",
              userEntityId: () => store.state.authUser?.user?.entityId,
              userRoleId: () => store.state.authUser?.user?.roleId,
            },
          ],
        ]),
      },
    },
    {
      path: ":entityId/extension-entregas",
      name: "admin-entities-submission-extensions",
      component: () =>
        import(
          /* webpackChunkName: "admin" */ "../../views/admin/entity/documentation/submission-period-extensions/Index.vue"
        ),
      props: (route) => ({
        ...numericParser(route, "entityId"),
      }),
      meta: {
        middlewares: [authorizationMiddleware],
        permissions: new Map([
          //TODO: cambiar policy a entity-doc-settings
          [
            "period-extension/manage",
            {
              entityId: "from-params",
              userRoleId: () => store.state.authUser?.user?.roleId,
            },
          ],
        ]),
      },
    },
    {
      path: ":entityId/entregas/:entityDocId/:period/:departmentId?",
      name: "admin-entities-doc-submission-show",
      component: () =>
        import(
          /* webpackChunkName: "admin" */ "../../views/admin/entity/documentation/submission/Show.vue"
        ),
      props: (route) => ({
        ...numericParser(route, "entityId"),
        ...numericParser(route, "entityDocId"),
        ...monthPeriodParser(route, "period"),
        ...nullableNumericParser(route, "departmentId"),
      }),
      meta: {
        middlewares: [authorizationMiddleware],
        permissions: new Map([
          [
            "submission/view",
            {
              entityId: "from-params",
              userEntityId: () => store.state.authUser?.user?.entityId,
              userRoleId: () => store.state.authUser?.user?.roleId,
            },
          ],
        ]),
      },
    },
    {
      path: ":entityId/cumplimiento/:period?",
      name: "admin-entity-compliance",
      component: () =>
        import(
          /* webpackChunkName: "admin" */ "../../views/admin/entity/Compliance.vue"
        ),
      props: (route) => ({
        ...numericParser(route, "entityId"),
        ...nullableMonthPeriodParser(route, "period"),
      }),
      meta: {
        middlewares: [authorizationMiddleware],
        permissions: new Map([
          [
            "entity/view",
            {
              entityId: "from-params",
              userEntityId: () => store.state.authUser?.user?.entityId,
              userRoleId: () => store.state.authUser?.user?.roleId,
            },
          ],
        ]),
      },
    },
  ],
};
export default routes;
